import { useApp } from '@/context/AppContext';
import { StatusBadge, Badge } from '@/components/ui/Badge';

export default function Billing() {
  const { invoices, custById, markPaid, openModal } = useApp();

  const paid   = invoices.filter(i => i.status === 'paid').reduce((a, i) => a + i.amount, 0);
  const overdue = invoices.filter(i => i.status === 'overdue').reduce((a, i) => a + i.amount, 0);
  const sent    = invoices.filter(i => i.status === 'sent').reduce((a, i) => a + i.amount, 0);
  const avg     = invoices.length > 0 ? Math.round(invoices.reduce((a, i) => a + i.amount, 0) / invoices.length) : 0;
  const net     = Math.round(paid * 0.971);
  const fees    = Math.round(paid * 0.029);

  return (
    <div className="page fade-up">
      <div className="ph">
        <div>
          <div className="ptitle">Billing &amp; Payments</div>
          <div className="psub">
            ${paid.toLocaleString()} collected · ${overdue.toLocaleString()} overdue
          </div>
        </div>
        <div className="pacts">
          <button className="btn btn-ghost btn-sm" onClick={() => openModal('export', { entity: 'invoices' })}>Export</button>
          <button className="btn btn-ghost btn-sm">Send Reminders</button>
          <button className="btn btn-primary btn-sm" onClick={() => openModal('add_invoice', {})}>
            + New Invoice
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div className="g4">
        <div className="kpi">
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--teal),transparent)' }} />
          <div className="kpi-label">Collected MTD</div>
          <div className="kpi-value">${paid.toLocaleString()}</div>
          <div className="kpi-sub">This month</div>
        </div>
        <div className="kpi">
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--cyan),transparent)' }} />
          <div className="kpi-label">Outstanding</div>
          <div className="kpi-value">${sent.toLocaleString()}</div>
          <div className="kpi-sub">{invoices.filter(i => i.status === 'sent').length} invoices sent</div>
        </div>
        <div className="kpi">
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--rose),transparent)' }} />
          <div className="kpi-label">Overdue</div>
          <div className="kpi-value">${overdue.toLocaleString()}</div>
          <div className="kpi-sub">{invoices.filter(i => i.status === 'overdue').length} accounts</div>
        </div>
        <div className="kpi">
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--violet),transparent)' }} />
          <div className="kpi-label">Avg Invoice</div>
          <div className="kpi-value">${avg}</div>
          <div className="kpi-sub">Per invoice</div>
        </div>
      </div>

      {/* Table + sidebar */}
      <div className="g7">
        <div className="card">
          <div className="card-head">
            <span className="card-title">Invoices</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <Badge color="var(--teal)" bg="var(--teal-t)">Paid</Badge>
              <Badge color="var(--amber)" bg="var(--amber-t)">Overdue</Badge>
              <Badge color="var(--cyan)" bg="var(--cyan-t)">Sent</Badge>
            </div>
          </div>
          <div className="card-body-0" style={{ overflowX: 'auto' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Customer</th>
                  <th>Service</th>
                  <th>Amount</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => {
                  const c = custById(inv.custId);
                  return (
                    <tr key={inv.id}>
                      <td>
                        <span className="mono" style={{ fontSize: 11, color: 'var(--t3)' }}>#{inv.id}</span>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{c?.name || ''}</div>
                      </td>
                      <td style={{ color: 'var(--t2)', fontSize: 12 }}>{inv.service}</td>
                      <td>
                        <span className="mono" style={{ fontWeight: 700 }}>${inv.amount}.00</span>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--t3)' }}>{inv.date}</td>
                      <td><StatusBadge status={inv.status} /></td>
                      <td>
                        {inv.status !== 'paid' ? (
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => markPaid(inv.id)}
                          >
                            Mark Paid
                          </button>
                        ) : (
                          <span style={{ fontSize: 11, color: 'var(--t3)' }}>Paid {inv.paidDate}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="card">
            <div className="card-head"><span className="card-title">May Fee Breakdown</span></div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--t3)' }}>Gross collected</span>
                <span className="mono">${paid.toLocaleString()}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--t3)' }}>Processing fees (~2.9%)</span>
                <span className="mono" style={{ color: 'var(--rose)' }}>-${fees.toLocaleString()}</span>
              </div>
              <div style={{ height: 1, background: 'var(--b1)' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                <span>Net revenue</span>
                <span className="mono" style={{ color: 'var(--teal)' }}>${net.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
